import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleDollarSign, Copy, Download, Receipt, TrendingUp } from 'lucide-react';

import { FIREBASE_CONFIG } from '../../config/Config';
import {
  getInvoiceSources,
  getInvoiceRef,
  buildPaymentBatch,
  subscribeClientInvoices,
} from '../../services/PortalFinanceiroRepository.js';
import PortalPageHeader from '../../components/portal/PortalPageHeader';
import { usePortalContext } from '../../context/PortalContext';
import { logClientPortalEvent, logClientPortalEventOnce } from '../../utils/portalEventLog';
import { checkFinanceIntegrity } from '../../utils/bidirectionalIntegrityGuard';

const PIX_CODE = import.meta.env.VITE_PIX_CODE || 'CHAVE_NAO_CONFIGURADA';

const toDateObj = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isFinite(raw.getTime()) ? raw : null;
  if (typeof raw?.toDate === 'function') {
    try {
      const d = raw.toDate();
      return d instanceof Date && Number.isFinite(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  const s = String(raw || '').trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d : null;
};

const formatCurrencyFromCents = (cents) => {
  const n = Number(cents);
  const safe = Number.isFinite(n) ? n : 0;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(safe / 100);
};

const normalizeInvoiceStatus = (row) => {
  const status = String(row?.status || '').trim().toUpperCase();
  if (status === 'PAID') return 'PAID';
  if (status === 'PENDING') return 'PENDING';
  return status || 'PENDING';
};

export default function PortalFinanceiro() {
  const { crmClient, loading: portalLoading } = usePortalContext();

  const clientId = useMemo(() => String(crmClient?.id || '').trim(), [crmClient?.id]);
  const appId = useMemo(() => String(FIREBASE_CONFIG?.projectId || '').trim(), []);
  const clientName = useMemo(() => {
    return (
      String(crmClient?.fullName || '').trim() ||
      String(crmClient?.qualification?.physical?.socialName || '').trim() ||
      String(crmClient?.qualification?.physical?.name || '').trim() ||
      String(crmClient?.contact?.name || '').trim() ||
      'Cliente'
    );
  }, [crmClient?.fullName, crmClient?.qualification?.physical?.socialName, crmClient?.qualification?.physical?.name, crmClient?.contact?.name]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [toast, setToast] = useState('');
  const [paying, setPaying] = useState(false);

  const lastClientIdRef = useRef(clientId);

  useEffect(() => {
    lastClientIdRef.current = clientId;
  }, [clientId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (portalLoading) return;
    if (!clientId) return;

    (async () => {
      try {
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_PORTAL_FINANCE_VIEWED',
          actorName: clientName,
          payload: {
            text: 'Cliente acessou a área financeira do portal.',
          },
          storage: 'session',
        });
      } catch (e) {
        void e;
      }
    })();
  }, [portalLoading, clientId, clientName]);

  useEffect(() => {
    if (portalLoading) return;

    const cid = String(clientId || '').trim();
    if (!cid) {
      setRows([]);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    const unsub = subscribeClientInvoices(
      cid,
      appId,
      (out) => {
        if (String(lastClientIdRef.current || '').trim() !== cid) return;
        out.sort((a, b) => {
          const ad = toDateObj(a?.dueDate);
          const bd = toDateObj(b?.dueDate);
          const av = ad ? ad.getTime() : 0;
          const bv = bd ? bd.getTime() : 0;
          return av - bv;
        });
        setRows(out);
        setLoading(false);
      },
      (e) => {
        if (String(lastClientIdRef.current || '').trim() !== cid) return;
        setError(String(e?.message || e));
        setLoading(false);
      }
    );

    return () => {
      try { unsub && unsub(); } catch {}
    };
  }, [portalLoading, clientId, appId]);

  useEffect(() => {
    if (!clientId || loading) return;
    checkFinanceIntegrity({ invoices: rows, clientId });
  }, [clientId, rows, loading]);

  const pending = useMemo(() => {
    const out = (Array.isArray(rows) ? rows : []).filter((r) => normalizeInvoiceStatus(r) !== 'PAID');
    out.sort((a, b) => {
      const ad = toDateObj(a?.dueDate);
      const bd = toDateObj(b?.dueDate);
      const av = ad ? ad.getTime() : 0;
      const bv = bd ? bd.getTime() : 0;
      return av - bv;
    });
    return out;
  }, [rows]);

  const paid = useMemo(() => {
    const out = (Array.isArray(rows) ? rows : []).filter((r) => normalizeInvoiceStatus(r) === 'PAID');
    out.sort((a, b) => {
      const ad = toDateObj(a?.paidAt) || toDateObj(a?.dueDate);
      const bd = toDateObj(b?.paidAt) || toDateObj(b?.dueDate);
      const av = ad ? ad.getTime() : 0;
      const bv = bd ? bd.getTime() : 0;
      return bv - av;
    });
    return out;
  }, [rows]);

  const todayKey = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }, []);

  const featuredInvoice = useMemo(() => {
    const all = Array.isArray(pending) ? pending : [];
    if (all.length === 0) return null;

    const scored = all
      .map((inv) => {
        const due = toDateObj(inv?.dueDate);
        const dueKey = due ? new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime() : 0;
        const overdue = dueKey ? dueKey < todayKey : false;
        return { inv, overdue, dueKey };
      })
      .sort((a, b) => {
        if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
        return (a.dueKey || 0) - (b.dueKey || 0);
      });

    return scored[0]?.inv || null;
  }, [pending, todayKey]);

  const isDev = Boolean(typeof import.meta !== 'undefined' && import.meta?.env && import.meta.env.DEV);

  const Shell = ({ title, subtitle, children }) => (
    <div className="space-y-4">
      <PortalPageHeader
        icon={<CircleDollarSign className="w-5 h-5 text-[#C5A059]" />}
        title={title}
        subtitle={subtitle}
      />
      {children}
    </div>
  );

  const Section = ({ title, hint, children, icon, accent }) => (
    <div className="border border-gray-200 bg-white rounded-lg overflow-hidden shadow-sm">
      <div className="px-4 py-2.5 border-b border-gray-200 bg-[#20243B] flex items-center gap-2">
        {icon && React.cloneElement(icon, { className: 'w-3.5 h-3.5 text-[#C5A059]' })}
        <div className="text-xs font-bold text-white">{title}</div>
        {hint ? <div className="text-[10px] text-[#C5A059]/80 truncate">· {hint}</div> : null}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );

  const InvoiceCard = ({ row, variant }) => {
    const due = toDateObj(row?.dueDate);
    const when = due ? due.toLocaleDateString('pt-BR') : '—';

    const status = normalizeInvoiceStatus(row);
    const isPaid = status === 'PAID';

    const overdue = (() => {
      if (isPaid) return false;
      if (!due) return false;
      const dueKey = new Date(due.getFullYear(), due.getMonth(), due.getDate()).getTime();
      return dueKey < todayKey;
    })();

    const amount = Number(row?.amountDue) || 0;
    const valueLabel = formatCurrencyFromCents(amount);

    const TYPE_LABELS = {
      OPERATING: 'Honorários',
      RETAINER: 'Adiantamento',
      TRUST: 'Depósito / Suprimento',
      REIMBURSEMENT: 'Reembolso de Despesas',
      EXPENSE: 'Despesa do Processo',
    };
    const typeRaw = String(row?.type || '').trim().toUpperCase();
    const typeLabel = TYPE_LABELS[typeRaw] || 'Honorários';

    const badgeClass = isPaid
      ? 'border-green-200 bg-green-50 text-green-700'
      : overdue
        ? 'border-red-200 bg-red-50 text-red-700'
        : 'border-[#C5A059]/40 bg-[#C5A059]/10 text-[#7a5a16]';

    return (
      <div className="border border-gray-300 rounded-lg p-2.5 bg-white">
        <div className="flex items-center justify-between gap-2 mb-1.5">
          <span className="text-xs font-semibold text-[#20243B]">{when}</span>
          <span className={`px-1.5 py-0.5 rounded border text-[9px] font-bold ${badgeClass}`}>
            {isPaid ? 'Pago' : overdue ? 'Vencido' : 'Pendente'}
          </span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[10px] text-gray-500">{typeLabel}</span>
          <span className={`text-sm font-bold ${
            variant === 'featured' ? 'text-[#20243B]' : isPaid ? 'text-green-700' : 'text-[#C5A059]'
          }`}>{valueLabel}</span>
        </div>
        {!isPaid && PIX_CODE && PIX_CODE !== 'CHAVE_NAO_CONFIGURADA' && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                try { navigator.clipboard.writeText(PIX_CODE); } catch {}
                const el = document.createElement('textarea');
                el.value = PIX_CODE;
                document.body.appendChild(el); el.select();
                try { document.execCommand('copy'); } catch {}
                document.body.removeChild(el);
                setToast(`Chave PIX copiada — valor: ${valueLabel}`);
                setTimeout(() => setToast(''), 3000);
              }}
              className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-[#C5A059]/40 bg-[#C5A059]/5 text-[11px] font-bold text-[#7a5a16] hover:bg-[#C5A059]/15 transition-colors"
            >
              <Copy className="w-3 h-3" />
              Copiar chave PIX — {valueLabel}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (portalLoading) {
    return (
      <Shell title="Financeiro" subtitle="Sincronizando seus dados...">
        <div className="border border-gray-300 shadow-sm bg-white rounded-md p-6 portal-loading-deferred">
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
            <div className="text-sm font-bold text-[#20243B]">Carregando...</div>
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell
      title="Financeiro"
      subtitle="Consulte suas faturas, recibos e formas de pagamento disponíveis."
    >
      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-xs text-red-800 whitespace-pre-wrap">{error}</div>
      ) : null}

      {toast ? (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-6 z-[999]">
          <div className="px-3 py-2 rounded-lg bg-[#20243B] text-white text-xs font-semibold shadow-lg">
            {toast}
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="border border-gray-300 bg-white rounded-lg px-3 py-2 portal-loading-deferred flex items-center gap-2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
          <div className="text-xs text-gray-500">Buscando faturas...</div>
        </div>
      ) : null}

      {/* ── Extrato Consolidado ───────────────────────────────────────────── */}
      {!loading && (pending.length > 0 || paid.length > 0) && (() => {
        const totalPago     = paid.reduce((s, r) => s + (Number(r?.amountDue) || 0), 0);
        const totalPendente = pending.reduce((s, r) => s + (Number(r?.amountDue) || 0), 0);
        const totalGeral    = totalPago + totalPendente;
        const pctPago       = totalGeral > 0 ? Math.round((totalPago / totalGeral) * 100) : 0;
        return (
          <div className="border border-gray-200 rounded-xl bg-gradient-to-br from-[#20243B] to-[#14172a] p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-3.5 h-3.5 text-[#C5A059]" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#C5A059]">Resumo financeiro</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">Total contratado</p>
                <p className="text-sm font-extrabold text-white mt-0.5 leading-tight">{formatCurrencyFromCents(totalGeral)}</p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wide text-emerald-400">Pago</p>
                <p className="text-sm font-extrabold text-emerald-300 mt-0.5 leading-tight">{formatCurrencyFromCents(totalPago)}</p>
              </div>
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wide text-amber-400">Pendente</p>
                <p className="text-sm font-extrabold text-amber-300 mt-0.5 leading-tight">{formatCurrencyFromCents(totalPendente)}</p>
              </div>
            </div>
            {totalGeral > 0 && (
              <div className="mt-3">
                <div className="flex justify-between mb-1">
                  <span className="text-[9px] text-gray-400">{pctPago}% quitado</span>
                  <span className="text-[9px] text-gray-400">{paid.length} fatura{paid.length !== 1 ? 's' : ''} paga{paid.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full rounded-full bg-emerald-400 transition-all" style={{ width: `${pctPago}%` }} />
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {!loading && featuredInvoice ? (
        <div className="space-y-2">
          <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Fatura em destaque</div>
          <InvoiceCard row={featuredInvoice} variant="featured" />

          {String(featuredInvoice?.paymentIntent?.method || '').trim().toUpperCase() === 'PIX' ? (
            <div className="space-y-1.5">
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(PIX_CODE);
                    setToast('Código Copiado! Abra o seu banco');
                    try {
                      await logClientPortalEvent({
                        clientId,
                        type: 'CLIENT_FINANCIAL_PIX_COPIED',
                        actorName: clientName,
                        payload: {
                          invoiceId: String(featuredInvoice?.id || ''),
                          text: 'Cliente copiou o código Pix de uma fatura no portal.',
                        },
                      });
                    } catch (e) {
                      void e;
                    }
                  } catch (e) {
                    setToast('Não foi possível copiar automaticamente.');
                    console.error(e);
                  }
                }}
                className="w-full py-2 rounded-lg bg-[#20243B] text-white text-xs font-bold uppercase tracking-wide hover:bg-[#1a1e30] transition-colors"
              >
                Copiar Código Pix
              </button>

              {isDev ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (paying) return;
                    const inv = featuredInvoice;
                    if (!inv?.id) return;
                    const status = normalizeInvoiceStatus(inv);
                    if (status === 'PAID') return;

                    setPaying(true);
                    try {
                      const sourceKey = String(inv?.__source || 'root').trim().toLowerCase();
                      const invoiceRef = getInvoiceRef(sourceKey, appId, inv.id);

                      const now = new Date();
                      const date = now.toISOString().slice(0, 10);
                      const competenceDate = now.toISOString().slice(0, 7);

                      const amountDue = Number(inv?.amountDue) || 0;
                      const type = String(inv?.type || '').trim().toUpperCase() || 'OPERATING';
                      const group = type === 'TRUST' ? 'CONTA_CLIENTE' : 'RECEITA_BRUTA';
                      const category = type === 'TRUST' ? 'Entrada de Suprimentos' : 'Honorários Contratuais';

                      const ledgerPayload = {
                        invoiceId: String(inv.id),
                        postedAt: serverTimestamp(),
                        fundType: type,
                        clientId: String(inv?.clientId || ''),
                        clientName: String(inv?.clientName || ''),
                        entries: [
                          { account: 'CASH', type: 'DEBIT', amount: amountDue },
                          { account: 'ACCOUNTS_RECEIVABLE', type: 'CREDIT', amount: amountDue },
                        ],

                        date,
                        competenceDate,
                        description: `Liquidação de Fatura ${String(inv.id)}`,
                        group,
                        category,
                        movementType: 'CREDIT',
                        status: 'PAID',
                        linkedToType: 'CLIENT',
                        linkedId: String(inv?.clientId || ''),
                        linkedName: String(inv?.clientName || ''),
                        valueCents: amountDue,
                        value: amountDue / 100,
                        module: 'portal',
                      };

                      const batch = buildPaymentBatch(invoiceRef, ledgerPayload, appId);

                      await batch.commit();
                      try {
                        await logClientPortalEvent({
                          clientId,
                          type: 'CLIENT_PORTAL_PAYMENT_SIMULATED',
                          actorName: clientName,
                          payload: {
                            invoiceId: String(inv?.id || ''),
                            amountDue,
                            text: `Cliente simulou o pagamento da fatura ${String(inv?.id || '').trim() || 'sem identificação'} no portal.`,
                          },
                        });
                      } catch (e) {
                        void e;
                      }
                      setToast('Pagamento simulado e registrado.');
                    } catch (e) {
                      console.error(e);
                      setToast('Falha ao simular pagamento.');
                    } finally {
                      setPaying(false);
                    }
                  }}
                  className={
                    'w-full py-1.5 rounded border text-[10px] font-medium ' +
                    (paying
                      ? 'border-slate-200 bg-slate-100 text-slate-400'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
                  }
                  disabled={paying}
                >
                  {paying ? 'Processando...' : 'Simular Pagamento (Dev/Teste)'}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      <Section
        title="Faturas Pendentes"
        hint="Itens vencidos ou a vencer."
        accent={{ borderColor: '#C5A05966', backgroundColor: '#C5A05912', color: '#7a5a16' }}
        icon={<Receipt className="w-4 h-4 text-[#C5A059]" />}
      >
        {!loading && pending.length === 0 ? (
          <div className="text-[12px] text-gray-500 py-2">Nenhuma fatura pendente no momento.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {pending.map((r, idx) => (
              <InvoiceCard key={String(r?.id || r?.invoiceId || idx)} row={r} />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Recibos Pagos"
        hint="Pagamentos já liquidados."
        accent={{ borderColor: '#86efac', backgroundColor: '#dcfce7', color: '#166534' }}
        icon={<Download className="w-4 h-4 text-green-600" />}
      >
        {!loading && paid.length === 0 ? (
          <div className="text-[12px] text-gray-500 py-2">Nenhum pagamento liquidado encontrado.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {paid.map((r, idx) => (
              <InvoiceCard key={String(r?.id || r?.invoiceId || idx)} row={r} />
            ))}
          </div>
        )}
      </Section>
    </Shell>
  );
}
